import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { villaAPI, calendarAPI, pricingAPI, houseRulesAPI } from '../config/api';
import api from '../config/api';
import { FiCalendar, FiUsers, FiArrowRight, FiPlus, FiPercent, FiX, FiCheck, FiShield, FiUpload, FiMapPin, FiCoffee } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import SEO from '../components/SEO';
import FAQSection from '../components/FAQSection';
import { bookingFAQs } from '../data/faqs';
import { Calendar } from '../components/ui/calendar';
import { withBhk } from '../utils/villaName';

const API = process.env.REACT_APP_BACKEND_URL;
import { compressImageFile } from '../utils/compressImage';
import { useSettings } from '../context/SettingsContext';

const Booking = () => {
  const [searchParams] = useSearchParams();
  const [villas, setVillas] = useState([]);
  const [selectedVilla, setSelectedVilla] = useState(searchParams.get('villa') || '');
  const [checkIn, setCheckIn] = useState(() => {
    const d = searchParams.get('checkin');
    return d ? new Date(d + 'T00:00:00') : null;
  });
  const [checkOut, setCheckOut] = useState(() => {
    const d = searchParams.get('checkout');
    return d ? new Date(d + 'T00:00:00') : null;
  });
  const [adults, setAdults] = useState(() => {
    const a = searchParams.get('adults');
    return a ? parseInt(a, 10) : 2;
  });
  const [kids, setKids] = useState(() => {
    const k = searchParams.get('kids');
    return k ? parseInt(k, 10) : 0;
  });
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(null); // 'checkin' | 'checkout' | null
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [blockedLoading, setBlockedLoading] = useState(false);
  const navigate = useNavigate();
  const { nightDiscountPct, nightDiscountEnabled } = useSettings();

  useEffect(() => {
    window.scrollTo(0, 0);
    villaAPI.getAll()
      .then(res => setVillas(res.data))
      .catch(() => {});
  }, []);

  // Helper: convert Calendar date to YYYY-MM-DD string using local components
  const toDateStr = (date) => {
    const yy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  // Fetch blocked dates when villa changes — store as YYYY-MM-DD strings to avoid timezone issues
  useEffect(() => {
    if (!selectedVilla) { setBlockedDates(new Set()); return; }
    setBlockedLoading(true);
    // Also compute the public window (today .. today+90d) so we drop any
    // past dates that might slip through — defense in depth vs the API.
    const winStart = new Date(); winStart.setHours(0, 0, 0, 0);
    const winEnd = new Date(winStart); winEnd.setDate(winEnd.getDate() + 91);
    const winStartStr = `${winStart.getFullYear()}-${String(winStart.getMonth()+1).padStart(2,'0')}-${String(winStart.getDate()).padStart(2,'0')}`;
    const winEndStr = `${winEnd.getFullYear()}-${String(winEnd.getMonth()+1).padStart(2,'0')}-${String(winEnd.getDate()).padStart(2,'0')}`;
    calendarAPI.getAvailability(selectedVilla)
      .then(res => {
        const dateStrings = new Set();
        for (const b of (res.data.blocked_dates || [])) {
          const [sy, sm, sd] = b.start_date.split('-').map(Number);
          const [ey, em, ed] = b.end_date.split('-').map(Number);
          let cur = new Date(sy, sm - 1, sd);
          const end = new Date(ey, em - 1, ed);
          while (cur < end) {
            const yy = cur.getFullYear();
            const mm = String(cur.getMonth() + 1).padStart(2, '0');
            const dd = String(cur.getDate()).padStart(2, '0');
            const key = `${yy}-${mm}-${dd}`;
            if (key >= winStartStr && key <= winEndStr) dateStrings.add(key);
            cur.setDate(cur.getDate() + 1);
          }
        }
        setBlockedDates(dateStrings);
      })
      .catch(() => setBlockedDates(new Set()))
      .finally(() => setBlockedLoading(false));
  }, [selectedVilla]);


  const villa = villas.find(v => v.id === selectedVilla);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Public booking window: today .. today + 90 days. Nothing outside this
  // range is rendered on the calendar — prevents leaking past booking history.
  const horizon = new Date(today);
  horizon.setDate(horizon.getDate() + 90);
  // Checkout can be up to 1 day past horizon (guest departs day after last
  // bookable night) — allow it to be selectable
  const checkoutHorizon = new Date(today);
  checkoutHorizon.setDate(checkoutHorizon.getDate() + 91);

  const formatDate = (d) => {
    if (!d) return '';
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };
  const formatDisplay = (d) => {
    if (!d) return 'Select date';
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Auto-clear check-in date if it now falls in the blocked set (someone booked
  // the same day while the guest was browsing). We DO NOT auto-clear the
  // check-out date if it's blocked — checkout on a blocked day is legit
  // (same-day turnover: departing guest leaves before incoming guest arrives).
  useEffect(() => {
    if (blockedLoading) return;
    if (checkIn && blockedDates.has(toDateStr(checkIn))) {
      setCheckIn(null); setCheckOut(null); setAvailability(null); setPricing(null);
      return;
    }
    // If the currently-selected checkOut would require sleeping on a blocked
    // night (any night between check_in and check_out-1), then invalidate.
    if (checkIn && checkOut) {
      const cur = new Date(checkIn); cur.setHours(0,0,0,0);
      const target = new Date(checkOut); target.setHours(0,0,0,0);
      while (cur < target) {
        if (blockedDates.has(toDateStr(cur))) {
          setCheckOut(null); setAvailability(null); setPricing(null);
          return;
        }
        cur.setDate(cur.getDate() + 1);
      }
    }
    // Auto-check availability if all params came from URL (first load only)
    if (selectedVilla && checkIn && checkOut && !availability && !checking) {
      checkAvail();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedDates, blockedLoading]);

  const checkAvail = useCallback(async () => {
    if (!selectedVilla || !checkIn || !checkOut) return;
    setChecking(true);
    try {
      const res = await calendarAPI.checkAvailability(selectedVilla, formatDate(checkIn), formatDate(checkOut));
      setAvailability(res.data);
      if (res.data.available) {
        const pRes = await pricingAPI.preview({ villa_id: selectedVilla, check_in: formatDate(checkIn), check_out: formatDate(checkOut) });
        setPricing(pRes.data);
      }
    } catch {
      setAvailability({ available: false, error: true });
    } finally {
      setChecking(false);
    }
  }, [selectedVilla, checkIn, checkOut]);

  const addOneMoreNight = () => {
    if (!checkOut) return;
    const newCheckout = new Date(checkOut);
    newCheckout.setDate(newCheckout.getDate() + 1);
    setCheckOut(newCheckout);
    setAvailability(null);
    setPricing(null);
  };

  const nights = checkIn && checkOut ? Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)) : 0;
  const totalGuests = adults + kids;
  const extraGuests = villa ? Math.max(0, totalGuests - (villa.base_capacity || villa.capacity)) : 0;
  const extraCharges = extraGuests * 1000 * nights;
  const [maidDays, setMaidDays] = useState(0);
  const maidCharge = maidDays * 2000;
  const [bbqMeals, setBbqMeals] = useState(0);
  const [bbqExtraLabour, setBbqExtraLabour] = useState(0);
  const bbqPerMeal = 3000 + (bbqExtraLabour * 1000);
  const bbqCharge = bbqMeals * bbqPerMeal;
  const [bonfireSessions, setBonfireSessions] = useState(0);
  const bonfireCharge = bonfireSessions * 2000;
  // Optional dedicated cooking space amenity (new Feb 2026). Now PER DAY.
  // Default days = nights when toggled on. Clamped to [0, nights] via inline stepper.
  const [cookingEnabled, setCookingEnabled] = useState(true);
  const [cookingPricingMap, setCookingPricingMap] = useState({});
  const [cookingSpace, setCookingSpace] = useState(false);
  const [cookingDays, setCookingDays] = useState(0);
  const cookingPerDay = selectedVilla ? (cookingPricingMap[selectedVilla] || 0) : 0;
  const cookingCharge = cookingSpace ? cookingDays * cookingPerDay : 0;

  useEffect(() => {
    axios.get(`${API}/api/features/cooking-amenity`)
      .then(r => {
        setCookingEnabled(!!r.data?.enabled);
        setCookingPricingMap(r.data?.pricing || {});
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  // If the host turns the amenity OFF while a user is on the booking page, un-tick it.
  useEffect(() => { if (!cookingEnabled && cookingSpace) setCookingSpace(false); }, [cookingEnabled, cookingSpace]);

  // Reset maid/bbq/cooking days if exceeds nights
  useEffect(() => {
    if (nights > 0 && maidDays > nights) setMaidDays(nights);
    const maxMeals = Math.max(nights * 2, 0);
    if (nights > 0 && bbqMeals > maxMeals) setBbqMeals(maxMeals);
    if (nights > 0 && bonfireSessions > nights) setBonfireSessions(nights);
    if (nights > 0 && cookingDays > nights) setCookingDays(nights);
  }, [nights, maidDays, bbqMeals, bonfireSessions, cookingDays]);

  // When the cooking checkbox flips ON, default days = full stay.
  // When it flips OFF, zero out days.
  useEffect(() => {
    if (cookingSpace && cookingDays === 0 && nights > 0) setCookingDays(nights);
    if (!cookingSpace && cookingDays !== 0) setCookingDays(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookingSpace]);

  const [indemnityOpen, setIndemnityOpen] = useState(false);
  const [indemnityAccepted, setIndemnityAccepted] = useState(false);
  const [houseRules, setHouseRules] = useState([]);
  const [signatureFile, setSignatureFile] = useState(null);
  const [photoIdFile, setPhotoIdFile] = useState(null);
  const [sigStatus, setSigStatus] = useState(''); // 'compressing' | 'ready' | 'error'
  const [idStatus, setIdStatus] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [docUploading, setDocUploading] = useState(false);
  const [docUploaded, setDocUploaded] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  const [paidBookingId, setPaidBookingId] = useState('');
  const [paidPaymentId, setPaidPaymentId] = useState('');
  const [paymentOption, setPaymentOption] = useState('full'); // 'full' or 'half'

  // B2B booking agent (optional) — gated by the "Booking Via Agent?" toggle
  const [isB2BBooking, setIsB2BBooking] = useState(false);
  const [b2bAgentCode, setB2bAgentCode] = useState('');
  const [b2bAgentPhone, setB2bAgentPhone] = useState('');
  const [b2bAgentVerified, setB2bAgentVerified] = useState(null); // null | {name, code, phone}
  const [b2bAgentErr, setB2bAgentErr] = useState('');
  const [b2bAgentChecking, setB2bAgentChecking] = useState(false);

  const verifyB2BAgent = async () => {
    setB2bAgentErr('');
    setB2bAgentVerified(null);
    if (!b2bAgentCode.trim() || !b2bAgentPhone.trim()) {
      setB2bAgentErr('Enter both Agent Code and Agent Mobile Number');
      return false;
    }
    setB2bAgentChecking(true);
    try {
      const res = await api.post('/b2b/validate-agent', {
        code: b2bAgentCode.trim(),
        phone: b2bAgentPhone.trim(),
      });
      setB2bAgentVerified(res.data);
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Agent verification failed.';
      setB2bAgentErr(msg);
      return false;
    } finally {
      setB2bAgentChecking(false);
    }
  };
  const [cartSaved, setCartSaved] = useState(false);
  const [priceRevised, setPriceRevised] = useState(false);
  const [savedCartLoaded, setSavedCartLoaded] = useState(false);

  // Load saved cart on login
  useEffect(() => {
    if (!localStorage.getItem('token') || savedCartLoaded) return;
    setSavedCartLoaded(true);
    // Only load saved cart if no URL params present
    if (searchParams.get('villa')) return;
    api.get('/cart/load').then(res => {
      const cart = res.data?.cart;
      if (cart && cart.villa_id) {
        setSelectedVilla(cart.villa_id);
        if (cart.check_in) setCheckIn(new Date(cart.check_in + 'T00:00:00'));
        if (cart.check_out) setCheckOut(new Date(cart.check_out + 'T00:00:00'));
        if (cart.adults) setAdults(cart.adults);
        if (cart.kids) setKids(cart.kids);
        if (cart.maid_days) setMaidDays(cart.maid_days);
        if (cart.bbq_meals) setBbqMeals(cart.bbq_meals);
        if (cart.bbq_extra_labour) setBbqExtraLabour(cart.bbq_extra_labour);
        if (cart.bonfire_sessions) setBonfireSessions(cart.bonfire_sessions);
        if (cart.cooking_space) setCookingSpace(!!cart.cooking_space);
        if (typeof cart.cooking_days === 'number') setCookingDays(cart.cooking_days);
        if (cart.price_revised) setPriceRevised(true);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save cart when details change (for logged-in users)
  useEffect(() => {
    if (!localStorage.getItem('token') || !selectedVilla || !checkIn || !checkOut) return;
    const saveTimer = setTimeout(() => {
      api.post('/cart/save', {
        villa_id: selectedVilla,
        villa_name: villa ? withBhk(villa) : '',
        check_in: formatDate(checkIn),
        check_out: formatDate(checkOut),
        adults, kids,
        maid_days: maidDays,
        bbq_meals: bbqMeals,
        bbq_extra_labour: bbqExtraLabour,
        bonfire_sessions: bonfireSessions,
        cooking_space: cookingSpace,
        cooking_days: cookingDays,
        cooking_charge: cookingCharge,
        saved_base_price: villa?.base_price || 0,
        saved_weekend_price: villa?.weekend_price || 0,
      }).then(() => setCartSaved(true)).catch(() => {});
    }, 2000);
    return () => clearTimeout(saveTimer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVilla, checkIn, checkOut, adults, kids, maidDays, bbqMeals, bbqExtraLabour, bonfireSessions, cookingSpace, cookingDays, cookingCharge]);

  const grandTotalBeforeCoupon = pricing ? pricing.final_price + extraCharges + maidCharge + bbqCharge + bonfireCharge + cookingCharge + (pricing.security_deposit || 0) : 0;

  // Coupon state — guest enters code, /api/coupons/validate previews discount
  const [couponInput, setCouponInput] = useState('');
  const [couponApplied, setCouponApplied] = useState(null); // {code, discount_amount}
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponError, setCouponError] = useState('');

  const couponDiscount = couponApplied?.discount_amount || 0;
  const grandTotal = Math.max(0, grandTotalBeforeCoupon - couponDiscount);
  const payNowAmount = paymentOption === 'half' ? Math.ceil(grandTotal / 2) : grandTotal;

  const handleApplyCoupon = async () => {
    setCouponError('');
    const code = (couponInput || '').trim().toUpperCase();
    if (!code) { setCouponError('Enter a coupon code'); return; }
    setCouponBusy(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await api.post('/coupons/validate', {
        code,
        email: userData.email || '',
        phone: guestPhone || userData.phone || '',
      });
      if (res.data?.valid) {
        setCouponApplied({ code: res.data.code, discount_amount: res.data.discount_amount });
        setCouponError('');
      }
    } catch (err) {
      setCouponApplied(null);
      setCouponError(err.response?.data?.detail || 'Could not apply coupon');
    } finally {
      setCouponBusy(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(null);
    setCouponInput('');
    setCouponError('');
  };

  const handleRazorpayPayment = async () => {
    if (!pricing || !villa) return;
    // Guard: if guest marked "Booking via Agent", agent code & phone MUST be
    // verified before we accept any payment.
    if (isB2BBooking) {
      if (!b2bAgentVerified) {
        const ok = await verifyB2BAgent();
        if (!ok) return;
      }
    }
    setPaymentProcessing(true);
    try {
      // Step 1: Create the booking first
      const bookingRes = await api.post('/bookings/create-direct', {
        villa_id: villa.id,
        villa_name: withBhk(villa),
        check_in: formatDate(checkIn),
        check_out: formatDate(checkOut),
        adults,
        kids,
        nights,
        base_price: pricing.final_price,
        extra_guests: extraGuests,
        extra_charges: extraCharges,
        maid_days: maidDays,
        maid_charge: maidCharge,
        bbq_meals: bbqMeals,
        bbq_extra_labour: bbqExtraLabour,
        bbq_charge: bbqCharge,
        bonfire_sessions: bonfireSessions,
        bonfire_charge: bonfireCharge,
        cooking_space: cookingSpace,
        cooking_days: cookingDays,
        cooking_charge: cookingCharge,
        security_deposit: pricing.security_deposit || 0,
        grand_total: grandTotal,
        coupon_code: couponApplied?.code || null,
        coupon_discount: couponApplied?.discount_amount || 0,
        payment_type: paymentOption,
        is_b2b_booking: isB2BBooking,
        b2b_agent_code: isB2BBooking ? b2bAgentCode.trim() : null,
        b2b_agent_phone: isB2BBooking ? b2bAgentPhone.trim() : null,
      });
      const bookingId = bookingRes.data.booking_id;

      // Step 2: Create Razorpay order with real booking ID
      const orderRes = await api.post('/payments/create-order', {
        booking_id: bookingId,
        payment_type: paymentOption,
        amount: payNowAmount,
      });
      const { order_id, key_id } = orderRes.data;
      const userData = JSON.parse(localStorage.getItem('user') || '{}');

      const options = {
        key: key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: payNowAmount * 100,
        currency: 'INR',
        name: 'Royal Garden Villas',
        description: `${withBhk(villa)} - ${nights} Night${nights > 1 ? 's' : ''}${paymentOption === 'half' ? ' (50% Advance)' : ''}`,
        order_id: order_id,
        handler: async (response) => {
          try {
            await api.post('/payments/verify', {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: bookingId,
            });
            setPaymentDone(true);
            setPaidBookingId(bookingId);
            setPaidPaymentId(response.razorpay_payment_id || '');
            api.delete('/cart/clear').catch(() => {});
          } catch {
            alert('Payment verification failed. Please contact us at +91 9820088292.');
          }
          setPaymentProcessing(false);
        },
        prefill: {
          name: userData.full_name || '',
          email: userData.email || '',
          contact: guestPhone || userData.phone || '',
        },
        theme: { color: '#F57C20' },
        modal: { ondismiss: () => setPaymentProcessing(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not initiate payment. Please try again.');
      setPaymentProcessing(false);
    }
  };

  useEffect(() => {
    houseRulesAPI.get().then(res => setHouseRules(res.data.rules || [])).catch(() => {});
  }, []);

  const handleShareOnWhatsApp = () => {
    const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
    const ref = (paidBookingId || '').slice(0, 8).toUpperCase();
    const siteOrigin = 'https://royalgardenvillas.in';
    const villaUrl = villa ? `${siteOrigin}/villas/${villa.id}` : siteOrigin;
    const villaNameDisplay = withBhk(villa) || 'Villa';
    // Only include the amenities line if guest actually booked add-ons
    const hasAddons = (maidDays > 0) || (bbqMeals > 0) || (bonfireSessions > 0);
    const amenitiesLine = hasAddons ? '\n\nPool, BBQ, bonfire — all set! See you there 🔥🌿' : '';
    const message =
      `🎉 Just booked our Lonavala getaway at Royal Garden Villas!\n\n` +
      `🏡 *Villa:* ${villaNameDisplay}\n` +
      `📅 *Dates:* ${fmt(checkIn)} – ${fmt(checkOut)} (${nights} night${nights > 1 ? 's' : ''})\n` +
      `👥 *Group:* ${adults} adult${adults > 1 ? 's' : ''}${kids > 0 ? `, ${kids} kid${kids > 1 ? 's' : ''}` : ''}\n` +
      `🆔 *Booking Ref:* ${ref}\n\n` +
      `✨ Take a look at the villa we'll be staying at — photos, amenities & more:\n${villaUrl}` +
      amenitiesLine;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const handleShareLocationOnWhatsApp = () => {
    const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
    const mapsUrl = 'https://maps.google.com/?q=18.764728,73.435652';
    const villaNameDisplay = withBhk(villa) || 'Villa';
    const message =
      `📍 *Royal Garden Villas* — Lonavala\n\n` +
      `Here's the exact location on Google Maps:\n${mapsUrl}\n\n` +
      `🏡 Villa: ${villaNameDisplay}\n` +
      `📅 Check-in: ${fmt(checkIn)} from 1:00 PM\n\n` +
      `📞 Need help finding us? Call +91 98200 88292`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  /**
   * Build a comprehensive WhatsApp booking-receipt message (itemised
   * pricing + paid / outstanding) and open wa.me for a specific RGV team
   * number. Uses only data already in client state — no extra API call.
   */
  const buildTeamReceiptMessage = () => {
    const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
    const fmtShort = (iso) => {
      try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' }); }
      catch { return iso; }
    };
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const ref = (paidBookingId || '').slice(0, 8).toUpperCase();
    const villaNameDisplay = withBhk(villa) || 'Villa';
    const outstanding = Math.max(0, grandTotal - payNowAmount);
    const paymentLabel = paymentOption === 'half' ? '50% Advance' : 'Full Payment';

    const lines = [];
    lines.push(`🏡 *Royal Garden Villas — Booking Receipt*`);
    lines.push(`🆔 *Booking Ref:* ${ref}`);
    lines.push('');
    lines.push(`👤 *Guest:* ${userData.full_name || 'Guest'}`);
    if (userData.email) lines.push(`📧 *Email:* ${userData.email}`);
    if (guestPhone || userData.phone) lines.push(`📞 *Phone:* ${guestPhone || userData.phone}`);
    lines.push('');
    lines.push(`🏠 *Villa:* ${villaNameDisplay}`);
    lines.push(`📅 *Check-in:* ${fmt(checkIn)}`);
    lines.push(`📅 *Check-out:* ${fmt(checkOut)}`);
    lines.push(`🌙 *Nights:* ${nights}`);
    lines.push(`👥 *Guests:* ${adults} adult${adults !== 1 ? 's' : ''}${kids > 0 ? `, ${kids} kid${kids !== 1 ? 's' : ''}` : ''}`);
    lines.push('');
    lines.push(`💰 *Pricing Breakdown:*`);
    if (pricing?.nightly_details?.length) {
      pricing.nightly_details.forEach((n) => {
        const orig = Number(n.original_rate) || 0;
        const saved = Number(n.saved) || 0;
        const pct = n.discount_applied && orig ? Math.round((saved * 100) / orig) : 0;
        const tag = n.discount_applied ? ` (${pct}% off)` : '';
        lines.push(`• Night ${n.night} · ${fmtShort(n.date)} — Rs.${(n.final_rate || 0).toLocaleString()}${tag}`);
      });
      lines.push(`  ─ Room subtotal: Rs.${(pricing.final_price || 0).toLocaleString()}`);
    } else if (pricing?.final_price) {
      lines.push(`• Room (${nights} night${nights !== 1 ? 's' : ''}): Rs.${pricing.final_price.toLocaleString()}`);
    }
    if (extraCharges > 0) lines.push(`• Extra guests (${extraGuests} × Rs.1,000 × ${nights}n): Rs.${extraCharges.toLocaleString()}`);
    if (maidCharge > 0) lines.push(`• Extra Butler service (${maidDays} day${maidDays !== 1 ? 's' : ''} × Rs.2,000): Rs.${maidCharge.toLocaleString()}`);
    if (bbqCharge > 0) lines.push(`• BBQ service (${bbqMeals} meal${bbqMeals !== 1 ? 's' : ''}): Rs.${bbqCharge.toLocaleString()}`);
    if (bonfireCharge > 0) lines.push(`• Bonfire (${bonfireSessions} session${bonfireSessions !== 1 ? 's' : ''} × Rs.2,000): Rs.${bonfireCharge.toLocaleString()}`);
    if (cookingCharge > 0) lines.push(`• Dedicated cooking space (${cookingDays} day${cookingDays !== 1 ? 's' : ''} × Rs.${cookingPerDay.toLocaleString()}): Rs.${cookingCharge.toLocaleString()}`);
    if (pricing?.security_deposit) lines.push(`• Refundable deposit: Rs.${pricing.security_deposit.toLocaleString()}`);
    if (couponDiscount > 0 && couponApplied) lines.push(`🎟️ Coupon discount (${couponApplied.code}): – Rs.${couponDiscount.toLocaleString()}`);
    lines.push('');
    lines.push(`🧾 *Grand Total: Rs.${grandTotal.toLocaleString()}*`);
    lines.push(`✅ *Paid Now:* Rs.${payNowAmount.toLocaleString()} (${paymentLabel})`);
    if (outstanding > 0) {
      lines.push(`⏳ *Outstanding:* Rs.${outstanding.toLocaleString()}`);
      lines.push(`    (Due by 1:00 PM, one day before check-in)`);
    } else {
      lines.push(`✅ *Outstanding:* Rs.0 — Fully paid`);
    }
    if (paidPaymentId) {
      lines.push('');
      lines.push(`💳 *Payment ID:* ${paidPaymentId}`);
    }
    lines.push('');
    lines.push(`Shared from royalgardenvillas.in`);

    return lines.join('\n');
  };

  const handleSendReceiptToTeam = (phone) => {
    const message = buildTeamReceiptMessage();
    const cleanPhone = String(phone).replace(/\D/g, '');
    window.open(
      `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div data-testid="booking-page" className="pt-28">
      <SEO
        title="Book Your Lonavala Villa Online | Royal Garden Villas — Instant Confirmation"
        description={`Book your luxury private pool villa in Lonavala online with instant confirmation via Razorpay. Live availability, 50% advance option, transparent pricing${nightDiscountEnabled ? ` with ${nightDiscountPct}% OFF from your 2nd night onwards` : ''}. 3BHK to 12BHK villas.`}
        path="/booking"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Book Online', url: '/booking' },
        ]}
      />
      {/* Page Header */}
      <section className="py-14 md:py-18 bg-[#1B2432]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-3">Reservations</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Book Your <span className="text-[#F57C20] italic">Stay</span>
          </h1>
          <p className="text-white/60 mt-4 max-w-xl text-sm">
            Select your preferred villa and dates. Get instant availability and pricing{nightDiscountEnabled ? ` — Stay Longer, Pay Less with ${nightDiscountPct}% off from your 2nd night onwards` : ''}.
          </p>
        </div>
      </section>

      {/* Discount Strip — hidden when master toggle is off */}
      {nightDiscountEnabled && (
        <div data-testid="booking-discount-banner" className="bg-[#F57C20]/10 border-b border-[#F57C20]/20 py-3">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-center gap-3">
            <FiPercent className="w-4 h-4 text-[#F57C20]" />
            <span className="text-[#F57C20] text-sm font-semibold">Stay Longer, Pay Less: {nightDiscountPct}% OFF 2nd Night Onwards!</span>
          </div>
        </div>
      )}

      <section className="py-12 md:py-16 bg-[#F7F8FA]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Booking Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-100 shadow-lg">
                <h2 className="text-2xl text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Check <span className="text-[#F57C20] italic">Availability</span>
                </h2>

                {/* Villa Selection */}
                <div className="mb-5">
                  <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Select Villa</label>
                  <select
                    value={selectedVilla}
                    onChange={e => { setSelectedVilla(e.target.value); setAvailability(null); setPricing(null); }}
                    data-testid="booking-villa-select"
                    className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20] appearance-none"
                  >
                    <option value="">Choose a villa...</option>
                    {villas.map(v => (
                      <option key={v.id} value={v.id}>
                        {withBhk(v)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Selection with Calendar Popup */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div className="relative">
                    <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Check-in Date</label>
                    <button
                      onClick={() => setCalendarOpen(calendarOpen === 'checkin' ? null : 'checkin')}
                      data-testid="booking-checkin-btn"
                      className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-left text-sm focus:outline-none focus:border-[#F57C20] flex items-center justify-between"
                    >
                      <span className={checkIn ? 'text-[#1A1A2E]' : 'text-[#9CA3AF]'}>{formatDisplay(checkIn)}</span>
                      <FiCalendar className="w-4 h-4 text-[#F57C20]" />
                    </button>
                    {calendarOpen === 'checkin' && (
                      <div className="absolute z-50 top-full mt-2 left-0 bg-white border border-gray-200 rounded-xl shadow-xl p-1 calendar-popup-mobile">
                        <div className="flex justify-between items-center px-3 pt-2">
                          <span className="text-[#6B7280] text-xs">Select Check-in Date</span>
                          <button onClick={() => setCalendarOpen(null)} className="text-[#9CA3AF] hover:text-[#1A1A2E]"><FiX className="w-4 h-4" /></button>
                        </div>
                        {blockedLoading ? (
                          <div className="flex items-center justify-center py-12 px-8">
                            <div className="animate-spin w-5 h-5 border-2 border-[#F57C20] border-t-transparent rounded-full mr-2" />
                            <span className="text-[#6B7280] text-xs">Loading availability...</span>
                          </div>
                        ) : (
                          <Calendar
                            mode="single"
                            selected={checkIn}
                            onSelect={(date) => {
                              if (date && blockedDates.has(toDateStr(date))) return;
                              setCheckIn(date);
                              setCalendarOpen('checkout');
                              setAvailability(null);
                              setPricing(null);
                              if (checkOut && date && date >= checkOut) setCheckOut(null);
                            }}
                            disabled={(date) => {
                              if (date < today) return true;
                              return blockedDates.has(toDateStr(date));
                            }}
                            hidden={[{ after: horizon }]}
                            fromMonth={today}
                            toMonth={horizon}
                            modifiers={{ blocked: (date) => blockedDates.has(toDateStr(date)) }}
                            modifiersClassNames={{ blocked: 'rdp-day-blocked' }}
                            className="text-[#1A1A2E]"
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Check-out Date</label>
                    <button
                      onClick={() => setCalendarOpen(calendarOpen === 'checkout' ? null : 'checkout')}
                      data-testid="booking-checkout-btn"
                      className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-left text-sm focus:outline-none focus:border-[#F57C20] flex items-center justify-between"
                    >
                      <span className={checkOut ? 'text-[#1A1A2E]' : 'text-[#9CA3AF]'}>{formatDisplay(checkOut)}</span>
                      <FiCalendar className="w-4 h-4 text-[#F57C20]" />
                    </button>
                    {calendarOpen === 'checkout' && (
                      <div className="absolute z-50 top-full mt-2 left-0 bg-white border border-gray-200 rounded-xl shadow-xl p-1 calendar-popup-mobile">
                        <div className="flex justify-between items-center px-3 pt-2">
                          <span className="text-[#6B7280] text-xs">Select Check-out Date</span>
                          <button onClick={() => setCalendarOpen(null)} className="text-[#9CA3AF] hover:text-[#1A1A2E]"><FiX className="w-4 h-4" /></button>
                        </div>
                        {blockedLoading ? (
                          <div className="flex items-center justify-center py-12 px-8">
                            <div className="animate-spin w-5 h-5 border-2 border-[#F57C20] border-t-transparent rounded-full mr-2" />
                            <span className="text-[#6B7280] text-xs">Loading availability...</span>
                          </div>
                        ) : (
                          <Calendar
                            mode="single"
                            selected={checkOut}
                            onSelect={(date) => {
                              if (!date) return;
                              // Same-day-turnover rule: a checkout date is valid as long as
                              // every NIGHT the guest actually sleeps (check_in .. date-1)
                              // is free. The checkout day itself can be an incoming
                              // guest's check-in day — that's normal hotel turnover.
                              if (checkIn) {
                                const cur = new Date(checkIn); cur.setHours(0,0,0,0);
                                const target = new Date(date); target.setHours(0,0,0,0);
                                while (cur < target) {
                                  if (blockedDates.has(toDateStr(cur))) return;
                                  cur.setDate(cur.getDate() + 1);
                                }
                              }
                              setCheckOut(date); setCalendarOpen(null); setAvailability(null); setPricing(null);
                            }}
                            disabled={(date) => {
                              if (date <= (checkIn || today)) return true;
                              if (!checkIn) return blockedDates.has(toDateStr(date));
                              // Any stay-night (check_in ... date-1) blocked → disable this checkout date
                              const cur = new Date(checkIn); cur.setHours(0,0,0,0);
                              const target = new Date(date); target.setHours(0,0,0,0);
                              while (cur < target) {
                                if (blockedDates.has(toDateStr(cur))) return true;
                                cur.setDate(cur.getDate() + 1);
                              }
                              return false;
                            }}
                            modifiers={{ blocked: (date) => {
                              // Only mark as visually "blocked" if it's actually
                              // unusable as a checkout. Same-day-turnover dates
                              // (where a new guest checks in that day but the
                              // stay-nights are free) should look selectable.
                              if (!checkIn) return blockedDates.has(toDateStr(date));
                              if (date <= checkIn) return false;
                              const cur = new Date(checkIn); cur.setHours(0,0,0,0);
                              const target = new Date(date); target.setHours(0,0,0,0);
                              while (cur < target) {
                                if (blockedDates.has(toDateStr(cur))) return true;
                                cur.setDate(cur.getDate() + 1);
                              }
                              return false;
                            } }}
                            hidden={[{ after: checkoutHorizon }]}
                            fromMonth={checkIn || today}
                            toMonth={checkoutHorizon}
                            modifiersClassNames={{ blocked: 'rdp-day-blocked' }}
                            className="text-[#1A1A2E]"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Nights Info */}
                {nights > 0 && (
                  <div className="mb-5 flex items-center gap-3 text-sm">
                    <span className="text-[#6B7280]">{nights} night{nights > 1 ? 's' : ''} selected</span>
                    {nights < 4 && (
                      <button
                        onClick={addOneMoreNight}
                        data-testid="add-one-more-night-btn"
                        className="flex items-center gap-1.5 text-[#F57C20] text-xs font-medium hover:underline"
                      >
                        <FiPlus className="w-3.5 h-3.5" />
                        {nights === 1 ? `Add 1 more night & save ${nightDiscountPct}% on Night 2!` : `✓ ${nightDiscountPct}% discount applied on ${nights - 1} night${nights - 1 !== 1 ? 's' : ''}`}
                      </button>
                    )}
                  </div>
                )}

                {/* Guests */}
                <div className="grid grid-cols-2 gap-5 mb-6">
                  <div>
                    <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Adults</label>
                    <div className="flex items-center border border-gray-200 rounded-lg bg-[#F7F8FA] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setAdults(Math.max(1, adults - 1))}
                        data-testid="booking-adults-minus"
                        className="w-12 h-12 flex items-center justify-center text-lg font-bold text-[#F57C20] hover:bg-orange-50 active:bg-orange-100 transition-colors"
                      >-</button>
                      <span data-testid="booking-adults" className="flex-1 text-center text-[#1A1A2E] text-sm font-semibold">{adults}</span>
                      <button
                        type="button"
                        onClick={() => setAdults(adults + 1)}
                        data-testid="booking-adults-plus"
                        className="w-12 h-12 flex items-center justify-center text-lg font-bold text-[#F57C20] hover:bg-orange-50 active:bg-orange-100 transition-colors"
                      >+</button>
                    </div>
                  </div>
                  <div>
                    <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Kids (incl. in capacity)</label>
                    <div className="flex items-center border border-gray-200 rounded-lg bg-[#F7F8FA] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setKids(Math.max(0, kids - 1))}
                        data-testid="booking-kids-minus"
                        className="w-12 h-12 flex items-center justify-center text-lg font-bold text-[#F57C20] hover:bg-orange-50 active:bg-orange-100 transition-colors"
                      >-</button>
                      <span data-testid="booking-kids" className="flex-1 text-center text-[#1A1A2E] text-sm font-semibold">{kids}</span>
                      <button
                        type="button"
                        onClick={() => setKids(kids + 1)}
                        data-testid="booking-kids-plus"
                        className="w-12 h-12 flex items-center justify-center text-lg font-bold text-[#F57C20] hover:bg-orange-50 active:bg-orange-100 transition-colors"
                      >+</button>
                    </div>
                  </div>
                </div>

                {/* Extra Guest Info */}
                {extraGuests > 0 && (
                  <div data-testid="extra-guest-info" className="bg-blue-50 border border-blue-200 text-blue-700 text-xs px-4 py-3 mb-5 rounded-lg">
                    Additional guest would be provided with floor mattress, blanket and pillow. Extra charges: Rs.1,000/person/night.
                  </div>
                )}

                {villa && totalGuests > villa.capacity && (
                  <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 mb-5 rounded-lg">
                    Maximum capacity for this villa is {villa.capacity} guests (incl. kids).
                  </div>
                )}

                {/* Extra Butler Service Option */}
                <div className="mb-5">
                  <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">
                    Extra Butler Service (Optional) — Rs.2,000/day
                  </label>
                  <p className="text-[#9CA3AF] text-xs mb-2">
                    Dedicated maid from 1 PM to 9 PM to serve lunch, high tea & dinner. Serving charges only (not cooking/supply).
                  </p>
                  <div className="bg-[#FFF8F0] border border-[#F57C20]/20 rounded-lg p-2.5 mb-3" data-testid="maid-service-note">
                    <p className="text-[#1A1A2E] text-[11px] leading-relaxed">
                      <span className="font-semibold text-[#F57C20]">Please note:</span> General housekeeping is already included in your villa charges (<strong>staff available 10 AM – 6 PM only</strong>). This additional Butler Service is purely optional &mdash; opt in only if you want a dedicated helping-hand exclusively for your group.
                    </p>
                    <p className="text-[#1A1A2E] text-[11px] leading-relaxed mt-1.5">
                      <span className="font-semibold text-[#F57C20]">About the butler:</span> the butler helps in <strong>arranging and serving food</strong> &mdash; laying out meals, serving at the table, refilling &amp; clearing up. (The butler does not cook / supply groceries.)
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <select
                      value={maidDays}
                      onChange={e => setMaidDays(parseInt(e.target.value))}
                      data-testid="booking-maid-days"
                      className="bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]"
                    >
                      <option value={0}>Optional Butler Service</option>
                      {[...Array(Math.min(nights || 7, 7))].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1} day{i > 0 ? 's' : ''}</option>
                      ))}
                    </select>
                    {maidDays > 0 && <span className="text-[#F57C20] text-sm font-semibold">+Rs.{maidCharge.toLocaleString()}</span>}
                  </div>
                </div>

                {/* BBQ Service Option */}
                <div className="mb-5">
                  <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">
                    Barbeque Service (Optional) — Rs.3,000/meal
                  </label>
                  <p className="text-[#9CA3AF] text-xs mb-1">
                    Includes Sigdi, Metal Sticks, Charcoal & one Labour per meal - 2 hours. Food not included.
                    Additional labour +Rs.1,000 per person per meal if required.
                  </p>
                  <p className="text-[#F57C20]/70 text-[10px] mb-2 italic">
                    Note: Utmost care to be taken of the property to avoid deposit forfeiture.
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <div>
                      <span className="text-[#6B7280] text-[10px] uppercase tracking-wider block mb-1">Meals (max 2/day)</span>
                      <select
                        value={bbqMeals}
                        onChange={e => setBbqMeals(parseInt(e.target.value))}
                        data-testid="booking-bbq-meals"
                        className="bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]"
                      >
                        <option value={0}>No barbeque service</option>
                        {[...Array(Math.min((nights || 1) * 2, 14))].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1} meal{i > 0 ? 's' : ''}</option>
                        ))}
                      </select>
                    </div>
                    {bbqMeals > 0 && (
                      <div>
                        <span className="text-[#6B7280] text-[10px] uppercase tracking-wider block mb-1">Extra Labour (+Rs.1,000/person/meal)</span>
                        <select
                          value={bbqExtraLabour}
                          onChange={e => setBbqExtraLabour(parseInt(e.target.value))}
                          data-testid="booking-bbq-labour"
                          className="bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]"
                        >
                          <option value={0}>No extra labour</option>
                          <option value={1}>+1 Extra Labour (+Rs.1,000/meal)</option>
                          <option value={2}>+2 Extra Labour (+Rs.2,000/meal)</option>
                        </select>
                      </div>
                    )}
                    {bbqMeals > 0 && <span className="text-[#F57C20] text-sm font-semibold mt-4">+Rs.{bbqCharge.toLocaleString()}</span>}
                  </div>
                </div>

                {/* Bonfire Service Option */}
                <div className="mb-6">
                  <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">
                    Bonfire Service (Optional) — Rs.2,000/session
                  </label>
                  <p className="text-[#9CA3AF] text-xs mb-2">
                    Includes Sigdi and wooden sticks for a cozy bonfire experience.
                  </p>
                  <div className="flex items-center gap-4">
                    <select
                      value={bonfireSessions}
                      onChange={e => setBonfireSessions(parseInt(e.target.value))}
                      data-testid="booking-bonfire-sessions"
                      className="bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]"
                    >
                      <option value={0}>No bonfire service</option>
                      {[...Array(Math.min(nights || 7, 7))].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1} session{i > 0 ? 's' : ''}</option>
                      ))}
                    </select>
                    {bonfireSessions > 0 && <span className="text-[#F57C20] text-sm font-semibold">+Rs.{bonfireCharge.toLocaleString()}</span>}
                  </div>
                </div>

                {/* Cooking Space Amenity (NEW — Feb 2026) */}
                {cookingEnabled && (
                  <div className="mb-6" data-testid="booking-cooking-section">
                    <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2 flex items-center gap-2">
                      <FiCoffee className="w-3.5 h-3.5 text-[#F57C20]" />
                      Dedicated Cooking Space (Optional) — {cookingPerDay ? `Rs.${cookingPerDay.toLocaleString()}/day` : 'NEW'}
                    </label>
                    <p className="text-[#9CA3AF] text-xs mb-2">
                      Bring your own chef / <em>maharaj</em>. We provide the <span className="font-semibold text-[#1A1A2E]">space,
                      utensils, cooking gas, steel platform &amp; washing sink</span>. You bring the groceries.
                      Please hand over the space neat &amp; tidy with cleaned utensils.
                    </p>
                    <div className="flex items-center gap-4">
                      <select
                        value={cookingDays}
                        onChange={e => {
                          const v = parseInt(e.target.value) || 0;
                          setCookingDays(v);
                          setCookingSpace(v > 0);
                        }}
                        data-testid="booking-cooking-days"
                        disabled={!selectedVilla || !nights}
                        className="bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20] disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <option value={0}>No cooking space</option>
                        {[...Array(Math.max(nights || 0, 0))].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1} day{i > 0 ? 's' : ''}</option>
                        ))}
                      </select>
                      {cookingCharge > 0 && <span className="text-[#F57C20] text-sm font-semibold">+Rs.{cookingCharge.toLocaleString()}</span>}
                    </div>
                    <p className="text-[10px] text-[#6B7280] mt-2 leading-snug" data-testid="cooking-gas-note">
                      <strong>Note:</strong> Each villa has its own kitchen platform &amp; crockery with
                      induction stove &amp; heating microwave. <strong className="text-[#1A1A2E]">Gas cooking is NOT allowed
                      inside the villa premises</strong> — guests may order food directly from restaurants /
                      cloud kitchens / caterers / Swiggy / Zomato (home-delivery is available at the property).
                      Gas cooking is permitted only in the <em>optional pre-dedicated cooking space</em> outside
                      the villa, subject to it being availed &amp; paid in the booking.
                    </p>
                  </div>
                )}

                {/* Check Availability Button */}
                <button
                  onClick={checkAvail}
                  disabled={!selectedVilla || !checkIn || !checkOut || checking || (villa && totalGuests > villa.capacity)}
                  data-testid="check-availability-btn"
                  className="w-full bg-[#F57C20] text-white py-4 text-sm font-semibold tracking-wide rounded-lg hover:bg-[#E06A10] transition-all disabled:opacity-30"
                >
                  {checking ? 'Checking...' : 'Check Availability & Pricing'}
                </button>
              </div>

              {/* Availability & Pricing Result */}
              {availability && (
                <div className="mt-6 bg-white rounded-xl p-6 md:p-8 border border-gray-100 shadow-lg">
                  {availability.available ? (
                    <>
                      <div data-testid="availability-result" className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                        <div className="w-3 h-3 bg-green-500 rounded-full" />
                        <span className="text-green-600 text-sm font-medium">Villa is available for your selected dates!</span>
                      </div>

                      {pricing && (
                        <div data-testid="price-estimate">
                          <h3 className="text-xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                            Price <span className="text-[#F57C20] italic">Breakdown</span>
                          </h3>

                          {/* Nightly Details */}
                          {pricing.nightly_details && (
                            <div className="mb-4 space-y-2">
                              {pricing.nightly_details.map((n, i) => (
                                <div key={i} className="flex justify-between items-center text-sm py-1">
                                  <span className="text-[#6B7280]">
                                    Night {n.night} ({n.date}) {n.is_weekend ? '(Weekend)' : '(Weekday)'}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    {n.discount_applied && (
                                      <span className="text-[#9CA3AF] line-through text-xs">Rs.{n.original_rate.toLocaleString()}</span>
                                    )}
                                    <span className={n.discount_applied ? 'text-[#F57C20] font-semibold' : 'text-[#1A1A2E] font-medium'}>
                                      Rs.{n.final_rate.toLocaleString()}
                                    </span>
                                    {n.discount_applied && (
                                      <span className="bg-[#F57C20]/10 text-[#F57C20] text-[10px] px-2 py-0.5 font-semibold rounded">{nightDiscountPct}% OFF</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Totals */}
                          <div className="border-t border-gray-100 pt-4 space-y-2">
                            {pricing.night_discount_saved > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-[#F57C20] font-medium">Multi-night discount saved</span>
                                <span className="text-[#F57C20] font-semibold">-Rs.{pricing.night_discount_saved.toLocaleString()}</span>
                              </div>
                            )}
                            {extraGuests > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-[#6B7280]">{extraGuests} extra guest{extraGuests > 1 ? 's' : ''} x {nights} nights x Rs.1,000</span>
                                <span className="text-[#1A1A2E] font-medium">+Rs.{extraCharges.toLocaleString()}</span>
                              </div>
                            )}
                            {maidDays > 0 && (
                              <div className="flex justify-between items-center text-sm" data-testid="breakdown-butler-row">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-[#6B7280]">Extra Butler service</span>
                                  <div className="inline-flex items-center bg-white border border-gray-200 rounded-full overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => setMaidDays(d => Math.max(0, d - 1))}
                                      data-testid="breakdown-butler-dec"
                                      aria-label="Reduce butler days"
                                      className="w-6 h-6 flex items-center justify-center text-[#1A1A2E] hover:bg-gray-50"
                                    >−</button>
                                    <span className="min-w-[52px] text-center text-[11px] text-[#1A1A2E] font-semibold px-1">
                                      {maidDays} day{maidDays > 1 ? 's' : ''}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => setMaidDays(d => Math.min(nights, d + 1))}
                                      disabled={maidDays >= nights}
                                      data-testid="breakdown-butler-inc"
                                      aria-label="Add butler day"
                                      className="w-6 h-6 flex items-center justify-center text-[#1A1A2E] hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >+</button>
                                  </div>
                                  <span className="text-[10px] text-[#9CA3AF]">× Rs.2,000</span>
                                </div>
                                <span className="text-[#1A1A2E] font-medium">+Rs.{maidCharge.toLocaleString()}</span>
                              </div>
                            )}
                            {bbqMeals > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-[#6B7280]">BBQ service x {bbqMeals} meal{bbqMeals > 1 ? 's' : ''} x Rs.3,000{bbqExtraLabour > 0 ? ` + ${bbqExtraLabour} extra labour` : ''}</span>
                                <span className="text-[#1A1A2E] font-medium">+Rs.{bbqCharge.toLocaleString()}</span>
                              </div>
                            )}
                            {bonfireSessions > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-[#6B7280]">Bonfire x {bonfireSessions} session{bonfireSessions > 1 ? 's' : ''} x Rs.2,000</span>
                                <span className="text-[#1A1A2E] font-medium">+Rs.{bonfireCharge.toLocaleString()}</span>
                              </div>
                            )}
                            {cookingSpace && (
                              <div className="flex justify-between items-center text-sm" data-testid="breakdown-cooking-row">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-[#6B7280]">Dedicated cooking space</span>
                                  <div className="inline-flex items-center bg-white border border-gray-200 rounded-full overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const next = Math.max(0, cookingDays - 1);
                                        setCookingDays(next);
                                        if (next === 0) setCookingSpace(false);
                                      }}
                                      data-testid="breakdown-cooking-dec"
                                      aria-label="Reduce cooking days"
                                      className="w-6 h-6 flex items-center justify-center text-[#1A1A2E] hover:bg-gray-50"
                                    >−</button>
                                    <span className="min-w-[52px] text-center text-[11px] text-[#1A1A2E] font-semibold px-1">
                                      {cookingDays} day{cookingDays > 1 ? 's' : ''}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => setCookingDays(d => Math.min(nights, d + 1))}
                                      disabled={cookingDays >= nights}
                                      data-testid="breakdown-cooking-inc"
                                      aria-label="Add cooking day"
                                      className="w-6 h-6 flex items-center justify-center text-[#1A1A2E] hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >+</button>
                                  </div>
                                  <span className="text-[10px] text-[#9CA3AF]">× Rs.{cookingPerDay.toLocaleString()}</span>
                                </div>
                                <span className="text-[#1A1A2E] font-medium">+Rs.{cookingCharge.toLocaleString()}</span>
                              </div>
                            )}
                            <div className="flex justify-between pt-3 border-t border-gray-100">
                              <span className="text-[#1A1A2E] font-bold">Booking Amount</span>
                              <span className="text-[#1A1A2E] text-lg font-bold">
                                Rs.{(pricing.final_price + extraCharges + maidCharge + bbqCharge + bonfireCharge + cookingCharge).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between items-center mt-2 bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
                              <div>
                                <span className="text-green-700 text-sm font-medium">Security Deposit</span>
                                <span className="text-green-600 text-[10px] block">Refundable next day post checkout inspection</span>
                              </div>
                              <span className="text-green-700 font-bold">Rs.{pricing.security_deposit?.toLocaleString()}</span>
                            </div>

                            {/* Coupon code */}
                            <div className="mt-3 bg-[#F7F8FA] border border-gray-200 rounded-lg p-3" data-testid="coupon-box">
                              {couponApplied ? (
                                <div className="flex items-center justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-green-700 text-xs font-bold flex items-center gap-1.5">
                                      <FiCheck className="w-3.5 h-3.5" />
                                      Coupon <span className="font-mono">{couponApplied.code}</span> applied
                                    </p>
                                    <p className="text-green-600 text-[11px] mt-0.5">You save Rs.{couponApplied.discount_amount.toLocaleString()}</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={handleRemoveCoupon}
                                    data-testid="coupon-remove-btn"
                                    className="text-[10px] text-[#6B7280] hover:text-red-600 underline flex-shrink-0"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280] block mb-1.5">
                                    Have a coupon code?
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={couponInput}
                                      onChange={e => setCouponInput(e.target.value.toUpperCase())}
                                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleApplyCoupon(); } }}
                                      placeholder="e.g. RGV-XXXXXX"
                                      data-testid="coupon-input"
                                      className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#F57C20]"
                                    />
                                    <button
                                      type="button"
                                      onClick={handleApplyCoupon}
                                      disabled={couponBusy || !couponInput.trim()}
                                      data-testid="coupon-apply-btn"
                                      className="bg-[#1A1A2E] text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-[#2A2A3E] disabled:opacity-50 flex-shrink-0"
                                    >
                                      {couponBusy ? '...' : 'Apply'}
                                    </button>
                                  </div>
                                  {couponError && (
                                    <p className="text-red-600 text-[11px] mt-1.5" data-testid="coupon-error">{couponError}</p>
                                  )}
                                </>
                              )}
                            </div>

                            {couponDiscount > 0 && (
                              <div className="flex justify-between items-center mt-2 bg-green-50 border border-green-300 px-3 py-2 rounded-lg" data-testid="coupon-discount-row">
                                <span className="text-green-700 text-sm font-semibold">🎟️ Coupon discount ({couponApplied.code})</span>
                                <span className="text-green-700 font-bold">- Rs.{couponDiscount.toLocaleString()}</span>
                              </div>
                            )}

                            <div className="flex justify-between pt-3 mt-3 border-t-2 border-[#F57C20]/30">
                              <span className="text-[#1A1A2E] font-bold text-base">Total Payable</span>
                              <div className="text-right">
                                <span className="text-[#F57C20] text-xl font-bold block" data-testid="total-payable">
                                  Rs.{grandTotal.toLocaleString()}
                                </span>
                                <span className="text-[#9CA3AF] text-[10px]">Incl. refundable deposit</span>
                              </div>
                            </div>
                          </div>

                          {/* Add More Night CTA — 1 → N nights unlocks discount on Night 2+ */}
                          {nights === 1 && nightDiscountEnabled && (
                            <div data-testid="add-night-cta" className="mt-6 bg-[#F57C20]/5 border border-[#F57C20]/20 p-4 rounded-xl flex items-center justify-between">
                              <div>
                                <p className="text-[#F57C20] text-sm font-semibold">
                                  Stay Longer, Pay Less &mdash; {nightDiscountPct}% OFF 2nd Night Onwards!
                                </p>
                                <p className="text-[#6B7280] text-xs mt-1">
                                  Add another night. 1st night stays at actual price; every extra night gets a flat {nightDiscountPct}% off.
                                </p>
                              </div>
                              <button
                                onClick={addOneMoreNight}
                                data-testid="add-night-from-pricing-btn"
                                className="bg-[#F57C20] text-white px-5 py-2.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 flex-shrink-0 hover:bg-[#E06A10] transition-all"
                              >
                                <FiPlus className="w-3.5 h-3.5" /> Add Night
                              </button>
                            </div>
                          )}

                          <p className="text-[#9CA3AF] text-xs mt-4">
                            * Check-in: 1:00 PM | Check-out: 10:00 AM. Security deposit refunded within 1 working day post checkout.
                          </p>

                          {/* Payment Options Preview — visible BEFORE indemnity so guest knows both options exist */}
                          {!docUploaded && grandTotal > 0 && (
                            <div data-testid="payment-preview" className="mt-5 rounded-xl border border-[#F57C20]/25 bg-[#FFFCF7] p-4">
                              <p className="text-[#F57C20] text-[10px] font-bold tracking-widest uppercase mb-2">Payment Options</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div className="flex items-start gap-2 rounded-lg bg-white border border-gray-100 p-3">
                                  <span className="text-[#F57C20] text-lg leading-none">•</span>
                                  <div>
                                    <p className="text-[#1A1A2E] text-xs font-semibold">Pay 100% Now</p>
                                    <p className="text-[#F57C20] text-sm font-bold">Rs.{grandTotal.toLocaleString()}</p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2 rounded-lg bg-white border border-gray-100 p-3">
                                  <span className="text-[#F57C20] text-lg leading-none">•</span>
                                  <div>
                                    <p className="text-[#1A1A2E] text-xs font-semibold">Pay 50% Advance</p>
                                    <p className="text-[#F57C20] text-sm font-bold">Rs.{Math.ceil(grandTotal / 2).toLocaleString()} <span className="text-[#6B7280] text-[10px] font-normal">now</span></p>
                                    <p className="text-[#6B7280] text-[10px] mt-0.5">Balance Rs.{(grandTotal - Math.ceil(grandTotal / 2)).toLocaleString()} by 1 PM, day before check-in</p>
                                  </div>
                                </div>
                              </div>
                              <p className="text-[#6B7280] text-[10px] mt-2">
                                <span className="text-[#F57C20] font-semibold">Pick your preferred option below</span> after signing the Indemnity &amp; House Rules and uploading your documents.
                              </p>
                            </div>
                          )}

                          {/* Indemnity & Payment Section */}
                          {localStorage.getItem('token') && (
                            <div data-testid="indemnity-payment-section" className="mt-6 pt-6 border-t border-gray-100">
                              {/* Indemnity Checkbox */}
                              <div className="bg-[#FFF8F0] border border-[#F57C20]/20 p-4 rounded-xl mb-4">
                                <div className="flex items-start gap-3">
                                  <FiShield className="w-5 h-5 text-[#F57C20] flex-shrink-0 mt-0.5" />
                                  <div className="flex-1">
                                    <p className="text-sm text-[#1A1A2E] font-semibold mb-1">Indemnity & House Rules</p>
                                    <p className="text-xs text-[#6B7280] mb-3">
                                      Please read and acknowledge the indemnity form & house rules before proceeding to payment.
                                    </p>
                                    <button
                                      onClick={() => setIndemnityOpen(true)}
                                      data-testid="read-indemnity-btn"
                                      className="text-[#F57C20] text-xs font-semibold underline hover:text-[#E06A10] transition-colors mb-3 block"
                                    >
                                      Read Indemnity & House Rules
                                    </button>
                                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={indemnityAccepted}
                                        onChange={e => setIndemnityAccepted(e.target.checked)}
                                        data-testid="indemnity-accept-checkbox"
                                        className="w-4.5 h-4.5 accent-[#F57C20] rounded"
                                      />
                                      <span className="text-xs text-[#1A1A2E] font-medium">
                                        I Acknowledge and Agree to the Indemnity & House Rules
                                      </span>
                                    </label>
                                    <p className="text-[10px] text-[#9CA3AF] mt-2 italic">
                                      Note: Payment made is by itself an acknowledgement of Agreed Indemnity.
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Document Upload Section - after accepting indemnity */}
                              {indemnityAccepted && !docUploaded && (
                                <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4" data-testid="document-upload-section">
                                  <p className="text-sm text-[#1A1A2E] font-semibold mb-1">Upload Documents</p>
                                  <p className="text-xs text-[#6B7280] mb-4">Please upload your scanned signature and a government-issued photo ID.</p>

                                  <div className="space-y-4">
                                    <div>
                                      <label className="text-[#1A1A2E] text-[10px] font-semibold tracking-wider uppercase block mb-1">ID Number (Aadhar / PAN / Passport)</label>
                                      <input
                                        type="text"
                                        value={idNumber}
                                        onChange={e => setIdNumber(e.target.value)}
                                        placeholder="Enter your ID number"
                                        data-testid="indemnity-id-number"
                                        className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-2.5 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-[#1A1A2E] text-[10px] font-semibold tracking-wider uppercase block mb-1">Contact Phone</label>
                                      <input
                                        type="tel"
                                        value={guestPhone}
                                        onChange={e => setGuestPhone(e.target.value)}
                                        placeholder="Your phone number"
                                        data-testid="indemnity-phone"
                                        className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-2.5 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-[#1A1A2E] text-[10px] font-semibold tracking-wider uppercase block mb-1">Scanned Signature</label>
                                      <label className={`flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 cursor-pointer transition-colors ${signatureFile ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-[#F57C20]'}`}>
                                        <FiUpload className={`w-4 h-4 ${signatureFile ? 'text-green-600' : 'text-[#9CA3AF]'}`} />
                                        <span className={`text-xs ${signatureFile ? 'text-green-700 font-medium' : 'text-[#9CA3AF]'} truncate`}>
                                          {sigStatus === 'compressing' ? 'Optimising photo…' : (signatureFile ? `${signatureFile.name} (${(signatureFile.size / 1024).toFixed(0)} KB)` : 'Click to upload signature image')}
                                        </span>
                                        <input
                                          type="file"
                                          accept="image/*"
                                          className="hidden"
                                          data-testid="signature-upload"
                                          onChange={async e => {
                                            const f = e.target.files[0];
                                            if (!f) return;
                                            setSigStatus('compressing');
                                            const compressed = await compressImageFile(f);
                                            setSignatureFile(compressed);
                                            setSigStatus('ready');
                                          }}
                                        />
                                      </label>
                                    </div>
                                    <div>
                                      <label className="text-[#1A1A2E] text-[10px] font-semibold tracking-wider uppercase block mb-1">Photo ID (Aadhar / PAN / Passport / DL)</label>
                                      <label className={`flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 cursor-pointer transition-colors ${photoIdFile ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-[#F57C20]'}`}>
                                        <FiUpload className={`w-4 h-4 ${photoIdFile ? 'text-green-600' : 'text-[#9CA3AF]'}`} />
                                        <span className={`text-xs ${photoIdFile ? 'text-green-700 font-medium' : 'text-[#9CA3AF]'} truncate`}>
                                          {idStatus === 'compressing' ? 'Optimising photo…' : (photoIdFile ? `${photoIdFile.name} (${(photoIdFile.size / 1024).toFixed(0)} KB)` : 'Click to upload photo ID')}
                                        </span>
                                        <input
                                          type="file"
                                          accept="image/*"
                                          className="hidden"
                                          data-testid="photoid-upload"
                                          onChange={async e => {
                                            const f = e.target.files[0];
                                            if (!f) return;
                                            setIdStatus('compressing');
                                            const compressed = await compressImageFile(f);
                                            setPhotoIdFile(compressed);
                                            setIdStatus('ready');
                                          }}
                                        />
                                      </label>
                                      <p className="text-[10px] text-[#9CA3AF] mt-1.5 italic">Photos are auto-optimised in your browser — works even on slow networks.</p>
                                    </div>

                                    <button
                                      disabled={!signatureFile || !photoIdFile || !idNumber || !guestPhone || docUploading || sigStatus === 'compressing' || idStatus === 'compressing'}
                                      data-testid="upload-documents-btn"
                                      onClick={async () => {
                                        setDocUploading(true);
                                        try {
                                          const formData = new FormData();
                                          formData.append('signature_image', signatureFile);
                                          formData.append('photo_id', photoIdFile);
                                          const userData = JSON.parse(localStorage.getItem('user') || '{}');
                                          formData.append('full_name', userData.full_name || 'Guest');
                                          formData.append('email', userData.email || '');
                                          formData.append('phone', guestPhone);
                                          formData.append('id_number', idNumber);
                                          await api.post('/indemnity/upload-documents', formData, {
                                            headers: { 'Content-Type': 'multipart/form-data' }
                                          });
                                          setDocUploaded(true);
                                        } catch (err) {
                                          alert(err.response?.data?.detail || 'Upload failed. Please try again.');
                                        } finally {
                                          setDocUploading(false);
                                        }
                                      }}
                                      className={`w-full py-3 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
                                        signatureFile && photoIdFile && idNumber && guestPhone
                                          ? 'bg-[#1B2432] text-white hover:bg-[#2a3545]'
                                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                      }`}
                                    >
                                      {docUploading ? 'Uploading...' : <><FiUpload className="w-3.5 h-3.5" /> Upload & Submit Documents</>}
                                    </button>
                                  </div>
                                </div>
                              )}

                              {docUploaded && (
                                <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4 flex items-center gap-2" data-testid="doc-upload-success">
                                  <FiCheck className="w-4 h-4 text-green-600" />
                                  <span className="text-green-700 text-xs font-medium">Documents uploaded & emailed to admin successfully!</span>
                                </div>
                              )}

                              {/* Price Revision Notice */}
                              {priceRevised && (
                                <div className="bg-yellow-50 border border-yellow-300 rounded-xl px-4 py-3 mb-4" data-testid="price-revised-notice">
                                  <p className="text-yellow-800 text-xs font-semibold mb-1">Price Revised</p>
                                  <p className="text-yellow-700 text-[10px]">Prices have been updated since your last visit. The current rates are now applicable.</p>
                                </div>
                              )}

                              {/* Payment Options */}
                              {docUploaded && !paymentDone && (
                                <div className="mb-4" data-testid="payment-options">
                                  <p className="text-[#1A1A2E] text-xs font-semibold uppercase tracking-wider mb-3">Choose Payment Option</p>
                                  <div className="space-y-2">
                                    <label
                                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentOption === 'full' ? 'border-[#F57C20] bg-[#F57C20]/5' : 'border-gray-200 hover:border-gray-300'}`}
                                      data-testid="payment-option-full"
                                    >
                                      <input type="radio" name="payopt" value="full" checked={paymentOption === 'full'} onChange={() => setPaymentOption('full')} className="accent-[#F57C20]" />
                                      <div className="flex-1">
                                        <span className="text-[#1A1A2E] text-sm font-semibold">Pay 100% Now</span>
                                        <span className="text-[#F57C20] text-sm font-bold ml-2">Rs.{grandTotal.toLocaleString()}</span>
                                      </div>
                                      <FiCheck className={`w-4 h-4 ${paymentOption === 'full' ? 'text-[#F57C20]' : 'text-transparent'}`} />
                                    </label>
                                    <label
                                      className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentOption === 'half' ? 'border-[#F57C20] bg-[#F57C20]/5' : 'border-gray-200 hover:border-gray-300'}`}
                                      data-testid="payment-option-half"
                                    >
                                      <input type="radio" name="payopt" value="half" checked={paymentOption === 'half'} onChange={() => setPaymentOption('half')} className="accent-[#F57C20] mt-1" />
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-[#1A1A2E] text-sm font-semibold">Pay 50% Now</span>
                                          <span className="text-[#F57C20] text-sm font-bold">Rs.{Math.ceil(grandTotal / 2).toLocaleString()}</span>
                                        </div>
                                        <p className="text-[#6B7280] text-[10px] mt-1">Balance Rs.{(grandTotal - Math.ceil(grandTotal / 2)).toLocaleString()} due by 1:00 PM, one day before check-in</p>
                                      </div>
                                      <FiCheck className={`w-4 h-4 mt-1 ${paymentOption === 'half' ? 'text-[#F57C20]' : 'text-transparent'}`} />
                                    </label>
                                  </div>

                                  {paymentOption === 'half' && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3" data-testid="half-payment-warning">
                                      <p className="text-red-700 text-[11px] font-bold mb-1">Important - Balance Payment Terms:</p>
                                      <p className="text-red-600 text-[10px] leading-relaxed">
                                        Balance payment of Rs.{(grandTotal - Math.ceil(grandTotal / 2)).toLocaleString()} must be completed by <strong>1:00 PM, one day prior to check-in</strong>. 
                                        Failure to pay the balance by the deadline will result in <strong>automatic booking cancellation with NO REFUND</strong> of the advance amount paid. 
                                        It will be assumed as booking cancelled by guest.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* ===================== B2B Booking Agent (optional) ===================== */}
                              {!paymentDone && (
                                <div className="mb-6" data-testid="b2b-section">
                                  <label className="flex items-start gap-3 cursor-pointer group">
                                    <input
                                      type="checkbox"
                                      data-testid="b2b-toggle"
                                      checked={isB2BBooking}
                                      onChange={(e) => {
                                        setIsB2BBooking(e.target.checked);
                                        if (!e.target.checked) {
                                          setB2bAgentCode('');
                                          setB2bAgentPhone('');
                                          setB2bAgentVerified(null);
                                          setB2bAgentErr('');
                                        }
                                      }}
                                      className="mt-1 w-4 h-4 accent-[#F57C20] flex-shrink-0"
                                    />
                                    <div>
                                      <p className="text-[#1A1A2E] text-sm font-semibold">Booking Via Booking Agent?</p>
                                      <p className="text-[#6B7280] text-[11px] mt-0.5">Tick this box only if a registered travel/booking agent is handling your booking.</p>
                                    </div>
                                  </label>

                                  {isB2BBooking && (
                                    <div data-testid="b2b-inputs" className="mt-3 ml-7 bg-[#FFF7EC] border border-[#F57C20]/40 rounded-lg p-4">
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                          <label className="block text-[10px] font-bold uppercase tracking-wider text-[#6B7280] mb-1">Agent Code</label>
                                          <input
                                            type="text"
                                            data-testid="b2b-agent-code"
                                            value={b2bAgentCode}
                                            onChange={(e) => { setB2bAgentCode(e.target.value.toUpperCase()); setB2bAgentVerified(null); setB2bAgentErr(''); }}
                                            placeholder="e.g. RAHUL01"
                                            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[#1A1A2E] text-sm uppercase tracking-wide focus:outline-none focus:border-[#F57C20]"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-[10px] font-bold uppercase tracking-wider text-[#6B7280] mb-1">Agent Mobile</label>
                                          <input
                                            type="tel"
                                            data-testid="b2b-agent-phone"
                                            value={b2bAgentPhone}
                                            onChange={(e) => { setB2bAgentPhone(e.target.value); setB2bAgentVerified(null); setB2bAgentErr(''); }}
                                            placeholder="98XXXXXXXX"
                                            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20]"
                                          />
                                        </div>
                                      </div>
                                      {b2bAgentErr && (
                                        <p data-testid="b2b-err" className="text-red-600 text-xs mt-2 bg-red-50 border border-red-100 rounded px-2 py-1.5">{b2bAgentErr}</p>
                                      )}
                                      {b2bAgentVerified && (
                                        <p data-testid="b2b-verified" className="text-emerald-700 text-xs mt-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 flex items-center gap-1.5">
                                          <FiCheck className="w-3.5 h-3.5" /> Verified: <strong>{b2bAgentVerified.name}</strong> ({b2bAgentVerified.code})
                                        </p>
                                      )}
                                      {!b2bAgentVerified && (
                                        <button
                                          type="button"
                                          data-testid="b2b-verify-btn"
                                          onClick={verifyB2BAgent}
                                          disabled={b2bAgentChecking}
                                          className="mt-2 bg-[#1A1A2E] hover:bg-black text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
                                        >
                                          {b2bAgentChecking ? 'Verifying…' : 'Verify Agent'}
                                        </button>
                                      )}
                                      <p className="text-[10px] text-[#9CA3AF] mt-2">Only agents registered with Royal Garden Villas are accepted. Code is case-insensitive.</p>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Payment Button / Success */}
                              {paymentDone ? (
                                <div className="bg-green-50 border border-green-300 rounded-xl p-5 text-center">
                                  <FiCheck className="w-8 h-8 text-green-600 mx-auto mb-2" />
                                  <p className="text-green-700 text-sm font-bold mb-1">Payment Successful!</p>
                                  <p className="text-green-600 text-xs">
                                    {paymentOption === 'half'
                                      ? `Rs.${payNowAmount.toLocaleString()} paid (50% advance). Balance Rs.${(grandTotal - payNowAmount).toLocaleString()} due by 1 PM, one day before check-in.`
                                      : 'Your booking is confirmed. A confirmation email has been sent.'
                                    }
                                  </p>
                                  {paidBookingId && (
                                    <p className="text-green-700 text-[11px] font-semibold mt-2 tracking-wide">
                                      Booking Ref: <span className="font-mono">{paidBookingId.slice(0, 8).toUpperCase()}</span>
                                    </p>
                                  )}
                                  <div className="mt-4 pt-4 border-t border-green-200" data-testid="send-receipt-to-team-section">
                                    <p className="text-[#1B2432] text-[13px] font-bold mb-1">📩 Notify RGV team on WhatsApp</p>
                                    <p className="text-[#6B7280] text-[11px] mb-3 leading-snug">
                                      Send your full booking receipt (villa, dates, pricing, paid &amp; outstanding) to our team so we can prepare for your arrival.
                                    </p>
                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleSendReceiptToTeam('919820088292')}
                                        data-testid="send-receipt-owner-btn"
                                        className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] active:bg-[#179a4b] transition-colors text-white text-sm font-semibold px-5 py-3 rounded-xl shadow-md"
                                      >
                                        <FaWhatsapp className="w-5 h-5" />
                                        Send Receipt to RGV Office
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleSendReceiptToTeam('919137893277')}
                                        data-testid="send-receipt-manager-btn"
                                        className="flex-1 inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 transition-colors text-[#1B2432] text-sm font-semibold px-5 py-3 rounded-xl shadow-sm border-2 border-[#25D366]"
                                      >
                                        <FaWhatsapp className="w-5 h-5 text-[#25D366]" />
                                        Send to Manager
                                      </button>
                                    </div>
                                    <p className="text-[#9CA3AF] text-[9px] mt-2 text-center italic">Recommended — helps us serve you better</p>
                                  </div>

                                  <div className="mt-4 pt-4 border-t border-green-200">
                                    <p className="text-[#374151] text-[11px] mb-2">Or tell your family &amp; friends to join — bring the whole group!</p>
                                    <div className="flex flex-wrap items-center justify-center gap-2">
                                      <button
                                        type="button"
                                        onClick={handleShareOnWhatsApp}
                                        data-testid="share-booking-whatsapp-btn"
                                        className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-[#25D366] text-xs font-semibold px-4 py-2 rounded-full border border-[#25D366]/40"
                                      >
                                        <FaWhatsapp className="w-4 h-4" />
                                        Share booking
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleShareLocationOnWhatsApp}
                                        data-testid="share-location-whatsapp-btn"
                                        className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-[#1B2432] text-xs font-semibold px-4 py-2 rounded-full border border-gray-300"
                                      >
                                        <FiMapPin className="w-4 h-4 text-[#F57C20]" />
                                        Share location
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  disabled={!indemnityAccepted || !docUploaded || paymentProcessing}
                                  onClick={handleRazorpayPayment}
                                  data-testid="proceed-payment-btn"
                                  className={`w-full py-4 text-sm font-semibold rounded-xl transition-all flex items-center justify-center gap-2 ${
                                    indemnityAccepted && docUploaded
                                      ? 'bg-[#F57C20] text-white hover:bg-[#E06A10]'
                                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  }`}
                                >
                                  {paymentProcessing ? 'Processing...' : <><FiArrowRight className="w-4 h-4" /> Pay Rs.{payNowAmount.toLocaleString()}{paymentOption === 'half' ? ' (50% Advance)' : ''}</>}
                                </button>
                              )}
                              {(!indemnityAccepted || !docUploaded) && (
                                <p className="text-[#9CA3AF] text-[10px] text-center mt-2">
                                  {!indemnityAccepted ? 'Please accept the Indemnity & House Rules to enable payment' : 'Please upload your signature and photo ID to proceed'}
                                </p>
                              )}

                              {/* Price Disclaimer */}
                              <div className="mt-4 bg-[#F7F8FA] border border-gray-200 rounded-lg p-3">
                                <p className="text-[#9CA3AF] text-[9px] leading-relaxed italic">
                                  Royal Garden Villas reserves the right to change prices. New rates shall be applicable at the time of booking. Prices are subject to change without any prior intimation.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div data-testid="availability-result" className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full" />
                      <span className="text-red-600 text-sm font-medium">Sorry, the villa is not available for these dates. Please try different dates.</span>
                    </div>
                  )}
                </div>
              )}

              {/* Login prompt */}
              {!localStorage.getItem('token') && availability?.available && (
                <div className="mt-6 bg-white rounded-xl p-6 text-center border border-gray-100">
                  <p className="text-[#6B7280] text-sm mb-4">Please login or register to proceed with booking</p>
                  <Link
                    to={`/login?returnUrl=${encodeURIComponent(`/booking?villa=${selectedVilla}&checkin=${formatDate(checkIn)}&checkout=${formatDate(checkOut)}&adults=${adults}&kids=${kids}`)}`}
                    data-testid="booking-login-link"
                    className="border-2 border-[#F57C20] text-[#F57C20] px-8 py-3 text-sm font-semibold rounded-lg inline-flex items-center gap-2 hover:bg-[#F57C20] hover:text-white transition-all"
                  >
                    Login to Book <FiArrowRight />
                  </Link>
                  <p className="text-[#9CA3AF] text-xs mt-3">
                    New user?{' '}
                    <Link
                      to={`/register?returnUrl=${encodeURIComponent(`/booking?villa=${selectedVilla}&checkin=${formatDate(checkIn)}&checkout=${formatDate(checkOut)}&adults=${adults}&kids=${kids}`)}`}
                      className="text-[#F57C20] font-semibold hover:underline"
                    >
                      Register here
                    </Link>
                  </p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Night Discount Info */}
              {nightDiscountEnabled && <div className="bg-white rounded-xl p-6 mb-6 border border-gray-100 shadow-lg" data-testid="discount-sidebar">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center border-2 border-[#F57C20] text-[#F57C20] rounded-lg">
                    <span className="text-lg font-bold">{nightDiscountPct}%</span>
                  </div>
                  <div>
                    <p className="text-[#1A1A2E] text-sm font-bold">Stay Longer, Pay Less</p>
                    <p className="text-[#F57C20] text-xs">{nightDiscountPct}% off on every night from the 2nd onwards</p>
                  </div>
                </div>
                <div className="space-y-2 text-xs text-[#6B7280]">
                  <div className="flex justify-between py-1.5 border-b border-gray-100">
                    <span>Night 1</span><span className="text-[#1A1A2E] font-medium">Full Price</span>
                  </div>
                  <div className="flex justify-between py-1.5">
                    <span>Night 2 &amp; onwards</span><span className="text-[#F57C20] font-semibold">{nightDiscountPct}% OFF each</span>
                  </div>
                </div>
              </div>}

              {/* Quick Info */}
              <div className="bg-white rounded-xl p-6 mb-6 border border-gray-100">
                <p className="label-uppercase mb-3">Booking Info</p>
                <ul className="space-y-2 text-xs text-[#6B7280]">
                  <li>Check-in: 1:00 PM | Check-out: 10:00 AM</li>
                  <li>Extra guests: Rs.1,000/person/night</li>
                  <li>Security deposit: refundable within 1 day</li>
                  <li>100% online payment only</li>
                  <li>Free cancellation 7+ days before check-in</li>
                  <li>No cash accepted at property</li>
                </ul>
              </div>

              {/* Contact */}
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <p className="label-uppercase mb-3">Need Help?</p>
                <a href="tel:+919820088292" className="block text-[#6B7280] text-sm hover:text-[#F57C20] transition-colors mb-2">+91 9820088292</a>
                <a href="https://wa.me/919820088292" target="_blank" rel="noopener noreferrer" className="block text-[#F57C20] text-sm hover:underline">Chat on WhatsApp</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Indemnity Modal */}
      {indemnityOpen && (
        <div data-testid="indemnity-modal-overlay" className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setIndemnityOpen(false)}>
          <div data-testid="indemnity-modal" className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="text-lg text-[#1A1A2E] font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Indemnity & House Rules
                </h2>
                <p className="text-[#6B7280] text-xs mt-0.5">Royal Garden Villas, Lonavala</p>
              </div>
              <button onClick={() => setIndemnityOpen(false)} data-testid="indemnity-modal-close" className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-[#6B7280] hover:text-[#1A1A2E] transition-colors">
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="overflow-y-auto flex-1 p-6 space-y-6">
              {/* Important Notice */}
              <div className="bg-[#FFF8F0] border border-[#F57C20]/20 rounded-xl p-4">
                <p className="text-[#F57C20] text-xs font-bold uppercase tracking-wider mb-2">Important Notice</p>
                <p className="text-[#4B5563] text-xs leading-relaxed">
                  As this is a company-managed villa, all communication is entertained only within office hours of <strong>10 AM to 6 PM, Monday to Saturday only</strong>.
                </p>
              </div>

              {/* Pre-Arrival */}
              <div>
                <p className="text-xs text-[#4B5563] leading-relaxed">
                  Kindly go through our House Rules before check-in, which we kindly ask you to review before your arrival and adhere to the same.
                </p>
              </div>

              {/* Indemnity Declaration */}
              <div>
                <h3 className="text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-wider">Indemnity Declaration</h3>
                <div className="space-y-2.5 text-xs text-[#4B5563] leading-relaxed">
                  <p>1. I accept full responsibility for any damage to the property or its contents during my stay and agree to compensate for the same.</p>
                  <p>2. I understand that the security deposit will be refunded on the next day of checkout, post inspection, subject to no damages found.</p>
                  <p>3. I confirm that the maximum guest capacity for the booked villa will be strictly followed. Any additional undeclared guests may lead to booking cancellation.</p>
                  <p>4. I acknowledge that Royal Garden Villas, its owners, and staff are not liable for any injury, illness, loss, or damage to personal belongings during the stay.</p>
                  <p>5. I agree that all guests in my group are aware of and will abide by these terms and the house rules listed below.</p>
                  <p>6. I understand the cancellation and refund policies as stated on the website.</p>
                  <p>7. I confirm that I am of legal age and authorized to enter into this agreement on behalf of all guests in my booking.</p>
                  <p data-testid="indemnity-kitchen-clause">
                    8. <strong>Kitchen &amp; Cooking Space Guidelines:</strong> Each villa has its own kitchen platform &amp; crockery with
                    induction stove &amp; heating microwave. <strong>Gas cooking is NOT allowed inside the villa premises</strong> —
                    I may order food directly from restaurants / cloud kitchens / caterers / Swiggy / Zomato (home-delivery available at the property).
                    Gas cooking is permitted ONLY in the Optional pre-dedicated Cooking Space outside the villa, which must be availed &amp; paid as part of the booking.
                    If I have opted for this add-on, I (and any private chef / <em>maharaj</em> I have engaged) will maintain hygiene and hand over the kitchen,
                    appliances and utensils in a <strong>neat, clean and tidy condition</strong> prior to departure. If deep-cleaning is required post-checkout,
                    a cleaning charge of <strong>Rs.1,000 will be deducted from the security deposit</strong>.
                  </p>
                  <p data-testid="indemnity-power-clause">
                    9. <strong>Power Cuts &amp; Fluctuations:</strong> In case of random power cuts or voltage fluctuations locally,
                    <strong> no refund or deduction shall be entertained</strong> as the same is not in the management&apos;s control.
                    An inverter &amp; battery backup is available and will suffice basic electricity needs during an outage —
                    namely <strong>1 light, 1 fan &amp; 1 x 5A power socket in each room</strong>, until the battery backup lasts.
                    AC, geyser, induction stove, microwave &amp; high-wattage appliances will not run on the inverter.
                  </p>
                  <p data-testid="indemnity-wifi-clause">
                    10. <strong>Internet &amp; Wi-Fi:</strong> Internet / Wi-Fi has <strong>not been charged in the tariff</strong>
                    and is provided as a <strong>complimentary, best-effort service, subject to availability</strong>. In case of local
                    ISP outages, network degradation or unavailability, no refund, deduction or compensation shall be entertained as these
                    events are outside the management&apos;s control.
                  </p>
                </div>
              </div>

              {/* House Rules */}
              <div>
                <h3 className="text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-wider">House Rules</h3>
                <div className="space-y-2">
                  {houseRules.map((rule, i) => (
                    <div key={i} className="flex items-start gap-3 text-xs text-[#4B5563] leading-relaxed">
                      <div className="w-5 h-5 bg-[#F57C20]/10 text-[#F57C20] rounded flex items-center justify-center flex-shrink-0 text-[10px] font-bold mt-0.5">
                        {i + 1}
                      </div>
                      <span>{rule}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Food Policy */}
              <div>
                <h3 className="text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-wider">Food Policy</h3>
                <p className="text-xs text-[#4B5563] leading-relaxed">
                  Villa rent is not inclusive of food. Royal Garden Villas offers facility of direct ordering of food by Guests (No Monopoly) — to be ordered & paid directly by guests to Caterers / Cloud Kitchens, who take orders on KG basis and give free home delivery. Swiggy & Zomato also works. Kitchen is equipped with Crockery, Induction and Microwave. Contact details of Caterers & Restaurants available at Villa.
                </p>
              </div>

              {/* Housekeeping */}
              <div>
                <h3 className="text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-wider">Housekeeping</h3>
                <p className="text-xs text-[#4B5563] leading-relaxed">
                  Housekeeping staff is available <strong>only from 10:00 AM to 6:00 PM</strong>, on prior request. No housekeeping service is available outside these hours.
                </p>
              </div>

              {/* Payment Policy */}
              <div>
                <h3 className="text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-wider">Payment Policy</h3>
                <div className="space-y-1.5 text-xs text-[#4B5563] leading-relaxed">
                  <p>- <strong>No cash payments are accepted.</strong></p>
                  <p>- 100% online payment only via Payment Gateway on the website or GPay on 9820088292.</p>
                  <p>- Guest IDs and outstandings (if any) of Rent or Security Deposit must be paid at the earliest, <strong>latest one day prior to check-in by 1 PM</strong>, and share the same to us on WhatsApp so as to get credit confirmation from our accounts team.</p>
                  <p>- Security Deposit shall be refunded back on the next day of checkout, post inspection.</p>
                </div>
              </div>

              {/* Additional Charges */}
              <div>
                <h3 className="text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-wider">Additional Charges</h3>
                <div className="space-y-1.5 text-xs text-[#4B5563] leading-relaxed">
                  <p>- <strong>Additional Visitors Entry:</strong> Rs.1,000 per person (visitor night-stay not allowed).</p>
                  <p>- <strong>Late Checkout:</strong> Chargeable beyond complimentary half hour @ Rs.3,000 per hour.</p>
                </div>
              </div>

              {/* Contact During Stay */}
              <div>
                <h3 className="text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-wider">Contact During Stay</h3>
                <p className="text-xs text-[#4B5563] leading-relaxed">
                  Royal Garden Villas point of contact is <strong>91378 93277</strong> while check-in, within office hours between 10 AM to 6 PM Monday to Saturday only, for the purpose of location guidance and Guest ID collection.
                </p>
              </div>

              {/* Closing Note */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-xs text-green-700 leading-relaxed italic">
                  Royal Garden Villas genuinely look forward to your stay and hope you have a pleasant and much more enjoyable experience at our Villa.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-100 flex-shrink-0">
              <label className="flex items-center gap-3 cursor-pointer select-none mb-3">
                <input
                  type="checkbox"
                  checked={indemnityAccepted}
                  onChange={e => setIndemnityAccepted(e.target.checked)}
                  data-testid="indemnity-modal-checkbox"
                  className="w-5 h-5 accent-[#F57C20] rounded"
                />
                <span className="text-sm text-[#1A1A2E] font-semibold">I Acknowledge and Agree</span>
              </label>
              <p className="text-[10px] text-[#9CA3AF] italic mb-4">
                Note: Payment made is by itself an acknowledgement of Agreed Indemnity.
              </p>
              <button
                onClick={() => setIndemnityOpen(false)}
                data-testid="indemnity-modal-done"
                className={`w-full py-3 text-sm font-semibold rounded-xl transition-all flex items-center justify-center gap-2 ${
                  indemnityAccepted ? 'bg-[#F57C20] text-white hover:bg-[#E06A10]' : 'bg-[#1B2432] text-white hover:bg-[#2a3545]'
                }`}
              >
                {indemnityAccepted ? <><FiCheck className="w-4 h-4" /> Done</> : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== BOOKING PROCESS FAQ (with JSON-LD schema for AI-search) ===================== */}
      <FAQSection
        title="Booking &amp; Payment FAQ"
        subtitle="How online booking, payments, coupons, refunds and indemnity work at Royal Garden Villas."
        testid="booking-faq"
        items={bookingFAQs}
      />
    </div>
  );
};

export default Booking;
